/**
 * Типы и разбор ответов Chatwoot для раздела «Входящие».
 *
 * Одна и та же переписка приходит в трёх видах, и они ЧУТЬ разные:
 *  • список / show — app/views/api/v1/conversations/partials/_conversation.json.jbuilder
 *    (последнее сообщение в messages[0] И в last_non_activity_message, assignee_type в meta);
 *  • события ActionCable — app/presenters/conversations/event_data_presenter.rb
 *    (messages = последнее ОБЫЧНОЕ сообщение без заметок и служебных, channel на верхнем уровне,
 *    first_reply_created_at — дата, а не число);
 *  • вложение message.created — Message#push_event_data (conversation: {unread_count, …} внутри).
 * normalizeConversation сводит всё к одной форме.
 */

// ── Сообщения ───────────────────────────────────────────────────────────────

/** message_type в JSON — число: 0 входящее, 1 исходящее, 2 служебное, 3 шаблон. */
export const MESSAGE_INCOMING = 0;
export const MESSAGE_OUTGOING = 1;
export const MESSAGE_ACTIVITY = 2;
export const MESSAGE_TEMPLATE = 3;

export interface CwSender {
  id: number;
  name?: string;
  /** contact | user | agent_bot (push_event_data каждого). */
  type?: string;
  thumbnail?: string;
  avatar_url?: string;
}

export interface CwAttachment {
  id: number;
  message_id?: number;
  /** image | audio | video | file | location | fallback | contact | embed … */
  file_type: string;
  data_url?: string;
  thumb_url?: string;
  extension?: string | null;
  file_size?: number;
  width?: number | null;
  height?: number | null;
  /** Расшифровка голосового (Attachment#audio_metadata). */
  transcribed_text?: string;
  /** Состояние нашей расшифровки: custom/app/models/custom/voice_transcription_attachment.rb. */
  unia_transcript?: { state?: 'pending' | 'done' | 'failed' | string } | null;
  coordinates_lat?: number;
  coordinates_long?: number;
  fallback_title?: string;
}

export interface CwMessage {
  id: number;
  content: string | null;
  inbox_id?: number;
  conversation_id: number;
  message_type: number;
  content_type?: string;
  /** sent | delivered | read | failed */
  status?: string;
  content_attributes?: Record<string, unknown> | null;
  /** Секунды Unix. */
  created_at: number;
  private: boolean;
  source_id?: string | null;
  sender?: CwSender | null;
  attachments?: CwAttachment[];
  echo_id?: string;
  /** Внутри события message.created. */
  conversation?: { unread_count?: number; last_activity_at?: number; assignee_id?: number | null };
}

/** Сообщение в ленте: серверное или ещё не доставленное (оптимистичное). */
export interface UiMessage extends CwMessage {
  /** Только у своих неотправленных: sending — идёт, failed — не ушло. */
  local?: 'sending' | 'failed';
  localError?: string;
}

// ── Переписки ───────────────────────────────────────────────────────────────

export interface RawConversation {
  id: number;
  account_id?: number;
  inbox_id: number;
  status?: string;
  channel?: string;
  meta?: {
    sender?: { id?: number; name?: string; email?: string | null; phone_number?: string | null; thumbnail?: string; additional_attributes?: Record<string, unknown> };
    assignee?: { id?: number; name?: string; available_name?: string } | null;
    assignee_type?: string | null;
    channel?: string;
  };
  messages?: CwMessage[];
  last_non_activity_message?: CwMessage | null;
  unread_count?: number;
  timestamp?: number;
  last_activity_at?: number;
  waiting_since?: number | null;
  agent_last_seen_at?: number | string | null;
  first_reply_created_at?: number | string | null;
  custom_attributes?: Record<string, unknown> | null;
  can_reply?: boolean;
  created_at?: number;
}

export interface InboxConversation {
  /** display_id: уникален только внутри кабинета — всегда ходим парой (accountId, id). */
  id: number;
  accountId: number;
  inboxId: number;
  status: string;
  channelType: string;
  contact: { id: number; name: string; phone: string | null; email?: string | null };
  assigneeKind: 'User' | 'AgentBot' | null;
  assignee: { id: number; name: string } | null;
  lastMessage: CwMessage | null;
  unreadCount: number;
  lastActivityAt: number;
  waitingSince: number;
  /** 0 — на переписку ещё ни разу не ответили. */
  firstReplyAt: number;
  /** Внешний номер стадии воронки CRM (custom_attributes.crm_stage_id) = InboxStage.external_id. */
  crmStageId: string | null;
  /** Сделка CRM, которую завела связка с этой перепиской (custom_attributes.crm_deal_id). */
  crmDealId: string | null;
  canReply: boolean;
}

/** Секунды из числа / строки-даты / пустого значения. */
export function toUnix(v: number | string | null | undefined): number {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.floor(v) : 0;
  if (typeof v === 'string' && v) {
    const t = Date.parse(v);
    return Number.isFinite(t) ? Math.floor(t / 1000) : 0;
  }
  return 0;
}

/** Выбор последнего видимого сообщения: список даёт last_non_activity_message, события — messages[0]. */
function pickLastMessage(raw: RawConversation): CwMessage | null {
  const candidates = [raw.last_non_activity_message, ...(raw.messages ?? [])].filter(
    (m): m is CwMessage => !!m && m.message_type !== MESSAGE_ACTIVITY,
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (b.id > a.id ? b : a));
}

export function normalizeConversation(raw: RawConversation, fallbackAccountId: number): InboxConversation {
  const stage = raw.custom_attributes?.crm_stage_id;
  const sender = raw.meta?.sender;
  const assignee = raw.meta?.assignee;
  const kind = raw.meta?.assignee_type;
  return {
    id: raw.id,
    accountId: raw.account_id ?? fallbackAccountId,
    inboxId: raw.inbox_id,
    status: raw.status ?? 'open',
    channelType: raw.channel ?? raw.meta?.channel ?? '',
    contact: {
      id: sender?.id ?? 0,
      name: sender?.name?.trim() || sender?.phone_number || 'Без имени',
      phone: sender?.phone_number ?? null,
      email: sender?.email ?? null,
    },
    assigneeKind: assignee && (kind === 'AgentBot' || kind === 'User') ? kind : assignee ? 'User' : null,
    assignee: assignee && assignee.id !== undefined
      ? { id: assignee.id, name: assignee.available_name || assignee.name || '' }
      : null,
    lastMessage: pickLastMessage(raw),
    unreadCount: raw.unread_count ?? 0,
    lastActivityAt: raw.last_activity_at ?? raw.timestamp ?? 0,
    waitingSince: toUnix(raw.waiting_since),
    firstReplyAt: toUnix(raw.first_reply_created_at),
    crmStageId: typeof stage === 'string' && stage ? stage : stage != null ? String(stage) : null,
    crmDealId: typeof raw.custom_attributes?.crm_deal_id === 'string' && raw.custom_attributes.crm_deal_id ? raw.custom_attributes.crm_deal_id : null,
    canReply: raw.can_reply ?? true,
  };
}

// ── Каналы и стадии ─────────────────────────────────────────────────────────

export interface CwInboxFull {
  id: number;
  name: string;
  channel_type: string;
}

/** GET /inboxes/:id/stages (inboxes_controller#stages) — голый массив. */
export interface CwInboxStage {
  id: number;
  external_id: string;
  name: string;
  color: string | null;
  position: number;
  is_won?: boolean;
  is_lost?: boolean;
}

/** GET /conversations/unread_counts → { payload }. stages — наша надстройка (Custom::…::Counter). */
export interface CwUnreadCounts {
  all_count: number;
  inboxes: Record<string, number>;
  labels?: Record<string, number>;
  teams?: Record<string, number>;
  stages?: Record<string, number>;
}
